import React from 'react';
import { State } from '@progress/kendo-data-query';
import { useNavigate, useParams } from 'react-router-dom';
import { PageChangeEvent, Pager } from '@progress/kendo-react-data-tools';

import {
  DefaultGridSettings,
  DefaultPagerSettings
} from '../../../../constants/grid-settings';
import PopupMenu from '../../../../components/popupMenu';
import { ZoneEntity } from '../../../../types/game-document/';
import { GetResourceValue } from '../../../../utils/game-document';
import { GameDocumentContext } from '../../../../contexts/game-document';
import { generateZoneSnapshot } from '../../../../utils/generate-zone-snapshot';
import { MenuList } from '../../../../features/game-document/maps/map-images-window';

export interface CardViewDataStateChangeEvent {
  dataState: State;
}

export interface CardViewProps extends State {
  total?: number;
  data?: ZoneEntity[];
  onCopy?: (id: string) => void;
  onExport: (id: string) => void;
  onDelete: (id: string) => void;
  onDataStateChange?: (event: CardViewDataStateChangeEvent) => void;
}

const PreviewImage: React.FC<{ zone: ZoneEntity }> = ({ zone }) => {
  const [state] = React.useContext(GameDocumentContext);

  const [url, setUrl] = React.useState<string>('');

  React.useEffect(() => {
    let objectUrl: string | null = null;

    const resolveUrl = async () => {
      const mapAssId = state.gameDocument?.rules.worldMap.zones.find(
        (z) => z.zoneAssId === zone.id
      )?.mapAssId;
      if (!mapAssId) return;

      const zoneAsMap = state.gameDocument?.assets.maps?.find(
        (m) => m.id === mapAssId
      );
      if (!zoneAsMap) return;

      if (zoneAsMap.type === 'illustrated') {
        const previewUrl = GetResourceValue(
          state.gameDocument!,
          zoneAsMap.imageResId ?? ''
        );
        setUrl(previewUrl);
      } else {
        const zoneBoundary = zone.boundary;
        const blob = await generateZoneSnapshot(zoneBoundary);
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      }
    };

    resolveUrl();

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [zone, zone.id]);

  return (
    <>
      {url ? (
        <img
          src={url}
          width={'100%'}
          height={180}
          style={{
            objectFit: 'cover'
          }}
          alt={'zone-preview'}
        />
      ) : (
        <div
          style={{
            width: '100%',
            height: '180px',
            backgroundColor: '#d0d0d0',
            borderRadius: '4px 4px 0 0'
          }}
        />
      )}
    </>
  );
};

export const CardView = ({
  data,
  total = 0,
  onCopy,
  onDelete,
  onExport,
  ...props
}: CardViewProps) => {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [state] = React.useContext(GameDocumentContext);

  const menuList: MenuList[] = React.useMemo(
    () => [
      { classIcon: 'edit', textMenu: 'Edit', textClass: 'text-primary' },
      {
        classIcon: 'download_for_offline',
        textMenu: 'Export to JSON',
        textClass: 'text-info'
      },
      {
        classIcon: 'library_add_check',
        textMenu: 'Copy Zone Map to My Zone Map Library',
        textClass: 'text-primary'
      },
      { classIcon: 'delete', textMenu: 'Delete', textClass: 'text-danger' }
    ],
    []
  );

  const onMenuSelected = (id: string, menu: MenuList) => {
    if (!id) return;

    switch (menu.textMenu) {
      case 'Edit':
        navigate(`/designer/${gameId}/map/${id}`, {
          state: {
            zonePanel: 'detail'
          }
        });
        break;
      case 'Export to JSON':
        onExport(id);
        break;
      case 'Delete':
        onDelete(id);
        break;
      case 'Copy Zone Map to My Zone Map Library':
        onCopy?.(id);
        break;
    }
  };

  const handlePageChange = (event: PageChangeEvent) => {
    raiseDataStateChangeEvent({ skip: event.skip, take: event.take });
  };

  /**
   * Raise the Grid DataState change event. I.e. filters have changed.
   * @param moreData
   */
  const raiseDataStateChangeEvent = (moreData: State) => {
    props.onDataStateChange?.({
      dataState: { ...getDataState(), ...moreData }
    });
  };

  const getDataState = () => {
    return {
      filter: props.filter,
      sort: props.sort,
      skip: props.skip,
      take:
        props.take !== undefined ? props.take : DefaultGridSettings.pageSize,
      group: props.group
    };
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        minHeight: 0,
        overflow: 'hidden'
      }}>
      <div
        className={'row grid-card-library'}
        style={{
          flexGrow: 1,
          overflow: 'auto'
        }}>
        {data &&
          data.map((zone) => {
            return (
              <div
                className={
                  'col-xs-12 col-sm-6 col-md-4 d-flex cursor-pointer text-decoration-none'
                }
                key={zone.id}
                onClick={() =>
                  navigate(`/designer/${gameId}/map/${zone.id}`, {
                    state: {
                      zonePanel: 'detail'
                    }
                  })
                }>
                <div className={'card mt-4 card-view card-library flex-grow-1'}>
                  <PreviewImage zone={zone} />

                  <div className={'card-body pt-2 pb-2 pl-3 pr-3'}>
                    <div
                      className={
                        'd-flex justify-content-between align-items-center'
                      }>
                      <h3 className={'title link-primary cursor-pointer mb-0'}>
                        {GetResourceValue(
                          state.gameDocument!,
                          zone.titleResId ?? ''
                        )}
                      </h3>
                      <PopupMenu
                        id={parseInt(zone.id)}
                        menus={menuList}
                        onMenuSelected={(id, mn) => onMenuSelected(zone.id, mn)}
                      />
                    </div>

                    <hr className={'mt-1 mb-2'} />

                    <p className={'mb-1'} style={{ fontSize: '0.875rem' }}>
                      {zone.description}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
      </div>

      <div className={'row mt-3'}>
        <div className={'col-md-12'}>
          <Pager
            onPageChange={handlePageChange}
            total={total}
            take={
              props.take !== undefined
                ? props.take
                : DefaultGridSettings.pageSize
            }
            skip={props.skip !== undefined ? props.skip : 0}
            {...DefaultPagerSettings}
          />
        </div>
      </div>
    </div>
  );
};
